import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { SearchIcon, ChevronDown, Calendar } from 'assets/icons';

registerLocale('fr-FR', fr);

/**
 * Composant de barre de recherche avec select pour choisir le type de recherche
 * @param {string} searchValue - Valeur de la recherche
 * @param {function} onSearchChange - Fonction appelée lors du changement de la recherche
 * @param {string} searchType - Type de recherche sélectionné ('all', 'releve', 'interimaire', 'mission')
 * @param {function} onSearchTypeChange - Fonction appelée lors du changement du type de recherche
 * @param {string} placeholder - Placeholder pour l'input de recherche
 * @param {boolean} hideSelect - Si true, masque le select de type de recherche
 * @param {Array} customSearchTypes - Types de recherche personnalisés (optionnel)
 */
const SearchBar = ({
  searchValue,
  onSearchChange,
  searchType,
  onSearchTypeChange,
  placeholder = 'Rechercher...',
  hideSelect = false,
  customSearchTypes = null
}) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isMotifSelectOpen, setIsMotifSelectOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [motifDropdownPosition, setMotifDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef(null);
  const buttonRef = useRef(null);
  const motifSelectRef = useRef(null);
  const motifButtonRef = useRef(null);

  const motifs = [
    { value: '1', label: 'Accroissement temporaire d\'activité' },
    { value: '2', label: 'En attente de recrutement' },
    { value: '3', label: 'Remplacement d\'un salarié absent' },
    { value: '4', label: 'Contrat temporaire d\'usage' },
    { value: '5', label: 'Emploi à caractère saisonnier' }
  ];

  const defaultSearchTypes = [
    { value: 'interimaire', label: 'Intérimaire', placeholder: 'Rechercher un intérimaire...' },
    { value: 'periode', label: 'Période', placeholder: 'Rechercher par période...' },
    { value: 'motif', label: 'Motif', placeholder: 'Sélect motif' },
    { value: 'mission', label: 'Missions', placeholder: 'Rechercher une mission...' }
  ];

  const searchTypes = customSearchTypes || defaultSearchTypes;

  const selectedType = searchTypes.find(type => type.value === searchType) || searchTypes[0];
  const currentPlaceholder = selectedType?.placeholder || placeholder;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors du select de type de recherche
      if (isSelectOpen) {
        const dropdownElement = document.querySelector('[data-search-type-dropdown]');
        const isClickInside = (selectRef.current && selectRef.current.contains(event.target)) ||
                             (buttonRef.current && buttonRef.current.contains(event.target)) ||
                             (dropdownElement && dropdownElement.contains(event.target));
        if (!isClickInside) {
        setIsSelectOpen(false);
      }
      }
      // Vérifier si le clic est en dehors du select de motif
      if (isMotifSelectOpen) {
        const motifDropdownElement = document.querySelector('[data-motif-dropdown]');
        const isClickInside = (motifSelectRef.current && motifSelectRef.current.contains(event.target)) ||
                             (motifButtonRef.current && motifButtonRef.current.contains(event.target)) ||
                             (motifDropdownElement && motifDropdownElement.contains(event.target));
        if (!isClickInside) {
          setIsMotifSelectOpen(false);
        }
      }
    };

    if (isSelectOpen || isMotifSelectOpen) {
      // Utiliser un délai pour permettre au clic de se propager d'abord
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);

    if (isSelectOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }
      if (isMotifSelectOpen) {
        updateMotifDropdownPosition();
        window.addEventListener('scroll', updateMotifDropdownPosition, true);
        window.addEventListener('resize', updateMotifDropdownPosition);
    }

    return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateMotifDropdownPosition, true);
        window.removeEventListener('resize', updateMotifDropdownPosition);
    };
    }
  }, [isSelectOpen, isMotifSelectOpen]);

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const updateMotifDropdownPosition = () => {
    if (motifButtonRef.current) {
      const rect = motifButtonRef.current.getBoundingClientRect();
      setMotifDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleToggleSelect = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handleToggleMotifSelect = () => {
    setIsMotifSelectOpen(!isMotifSelectOpen);
  };

  const handleMotifSelect = (motif) => {
    onSearchChange(motif.label);
    setIsMotifSelectOpen(false);
  };

  return (
    <div className="flex flex-col tab:flex-row items-stretch tab:items-center gap-3 flex-shrink-0">
      {/* Select pour le type de recherche */}
      {!hideSelect && (
      <div className="relative" ref={selectRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggleSelect}
          className="flex items-center justify-between w-full tab:w-auto min-w-[160px] border border-gray-300 rounded-md bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <span>{selectedType.label}</span>
          <ChevronDown wh={16} color="#374151" />
        </button>
        {isSelectOpen && createPortal(
          <div 
            data-search-type-dropdown
            className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`
            }}
          >
            {searchTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Empêcher le blur du bouton
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSearchTypeChange(type.value);
                  if (type.value !== searchType) {
                    onSearchChange(''); // Réinitialiser la recherche quand on change de type
                  }
                  setIsSelectOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                  searchType === type.value ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
      )}

      {/* Input de recherche, Select de motif ou DatePicker pour période */}
      {searchType === 'motif' ? (
        <div className="relative w-[300px]" ref={motifSelectRef}>
          <button
            ref={motifButtonRef}
            type="button"
            onClick={handleToggleMotifSelect}
            className="w-full flex items-center justify-between min-h-[38px] border border-gray-300 rounded-md bg-white pl-3 pr-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="flex-1 text-left">{searchValue || 'Sélect motif'}</span>
            <ChevronDown wh={16} color="#374151" className="flex-shrink-0 ml-2" />
          </button>
          {isMotifSelectOpen && createPortal(
            <div 
              data-motif-dropdown
              className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${motifDropdownPosition.top}px`,
                left: `${motifDropdownPosition.left}px`,
                width: `${motifDropdownPosition.width}px`
              }}
            >
              <button
                type="button"
                onClick={() => {
                  onSearchChange('');
                  setIsMotifSelectOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors text-gray-700"
              >
                Tous les motifs
              </button>
              {motifs.map((motif) => (
                <button
                  key={motif.value}
                  type="button"
                  onClick={() => handleMotifSelect(motif)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                    searchValue === motif.label ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                  }`}
                >
                  {motif.label}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
      ) : searchType === 'periode' ? (
        (() => {
          const parsePeriodValue = (val) => {
            if (!val || !val.trim()) return { start: null, end: null };
            const parts = val.split('|');
            const parseDate = (str) => {
              if (!str || !str.trim()) return null;
              const p = str.trim().split('/');
              if (p.length !== 3) return null;
              const day = parseInt(p[0], 10);
              const month = parseInt(p[1], 10) - 1;
              const year = parseInt(p[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            };
            return {
              start: parseDate(parts[0]),
              end: parts.length > 1 ? parseDate(parts[1]) : null
            };
          };
          const formatDate = (date) => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          };
          const { start, end } = parsePeriodValue(searchValue);
          return (
            <div className="relative w-[300px]">
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 pl-3 z-10 pointer-events-none">
                <Calendar wh={18} color="#6B7280" />
              </div>
              <DatePicker
                portalId="react-datepicker-portal"
                selectsRange
                startDate={start}
                endDate={end}
                onChange={(dates) => {
                  const [newStart, newEnd] = dates || [null, null];
                  if (newStart || newEnd) {
                    const s = newStart ? formatDate(newStart) : '';
                    const e = newEnd ? formatDate(newEnd) : '';
                    onSearchChange(`${s}|${e}`);
                  } else {
                    onSearchChange('');
                  }
                }}
                dateFormat="dd/MM/yyyy"
                locale="fr-FR"
                placeholderText="Du - Au"
                isClearable
                className="w-full border border-gray-300 rounded-md pl-9 pr-4 py-2 text-sm focus:border-yellow-500 focus:outline-none cursor-pointer"
              />
            </div>
          );
        })()
      ) : (
      <div className="relative w-[300px]">
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 p-1">
          <SearchIcon wh={20} color="#6B7280" />
        </div>
        <input
          type="text"
            placeholder={currentPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md pl-7 pr-4 py-2 text-sm focus:border-yellow-500 focus:outline-none"
        />
      </div>
      )}
    </div>
  );
};

export default SearchBar;

